import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import useDrivePicker from "react-google-drive-picker";
import { GOOGLE_API_KEY, GOOGLE_CLIENT_ID, getOAuthToken } from "./googleAuth";

const GooglePicker = ({ isRequested, onCSVBlobsCreated, onClose, oauthToken, setOauthToken }) => {
  const [openPicker] = useDrivePicker();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerScriptLoaded, setPickerScriptLoaded] = useState(false);
  const [sheetOptions, setSheetOptions] = useState([]);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);

  // Load Google Picker script
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = "https://apis.google.com/js/api.js";
      script.async = true;
      script.onload = () => setPickerScriptLoaded(true);
      document.body.appendChild(script);
      return () => {
        script.remove();
      };
    } else {
      setPickerScriptLoaded(true);
    }
  }, [pickerScriptLoaded]);

  // Fetch data for a specific sheet
  const fetchSheetData = React.useCallback(
    async (file, sheet) => {
      const SHEETS_API = "https://sheets.googleapis.com/v4/spreadsheets";
      const { rowCount = 1000, columnCount = 26 } = sheet.gridProperties || {};
      // Convert columnCount to column letter(s)
      const getColumnLetter = (col) => {
        let letter = "";
        while (col > 0) {
          let rem = (col - 1) % 26;
          letter = String.fromCharCode(65 + rem) + letter;
          col = Math.floor((col - 1) / 26);
        }
        return letter;
      };
      const lastColumn = getColumnLetter(columnCount);
      const lastRow = rowCount;
      const range = `${sheet.title}!A1:${lastColumn}${lastRow}`;
      try {
        const valuesResponse = await fetch(`${SHEETS_API}/${file.id}/values/${range}?key=${GOOGLE_API_KEY}`, {
          headers: {
            Authorization: `Bearer ${oauthToken}`,
          },
        });
        if (!valuesResponse.ok) {
          throw new Error("Failed to fetch spreadsheet values");
        }
        const values = await valuesResponse.json();
        const data = values.values || [];
        const csvContent = data.map((row) => row.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        // Add filename property to the blob so it can be accessed later
        blob.name = `${file.name} - ${sheet.title}`;
        return blob;
      } catch (error) {
        console.error("Error fetching sheet data:", error);
        return null;
      }
    },
    [oauthToken]
  );

  // Fetch spreadsheet data from selected file (from Picker)
  const handleSelectSpreadsheetFromPicker = React.useCallback(
    async (file) => {
      const SHEETS_API = "https://sheets.googleapis.com/v4/spreadsheets";
      try {
        const metadataResponse = await fetch(`${SHEETS_API}/${file.id}?key=${GOOGLE_API_KEY}`, {
          headers: {
            Authorization: `Bearer ${oauthToken}`,
          },
        });
        if (!metadataResponse.ok) {
          throw new Error("Failed to fetch spreadsheet metadata");
        }
        const spreadsheetMetaData = await metadataResponse.json();
        const sheets = spreadsheetMetaData.sheets || [];
        if (sheets.length > 1) {
          setSheetOptions(sheets.map((s) => s.properties));
          setPendingFile({ file, spreadsheetMetaData });
          return; // Wait for user to select sheet
        }
        const sheet = sheets[0].properties;
        const blob = await fetchSheetData(file, sheet);
        if (blob) {
          onCSVBlobsCreated([blob]);
        }
        onClose();
        setIsPickerOpen(false);
        setSheetOptions([]);
        setSelectedSheets([]);
        setPendingFile(null);
      } catch (error) {
        console.error("Error fetching spreadsheet data:", error);
      }
    },
    [oauthToken, fetchSheetData, onClose, onCSVBlobsCreated]
  );

  // Handle user selecting a sheet from the UI
  const handleSheetSelect = (sheetTitle) => {
    setSelectedSheets((prev) => {
      if (prev.includes(sheetTitle)) {
        return prev.filter((t) => t !== sheetTitle);
      }
      return [...prev, sheetTitle];
    });
  };

  const handleImportSelected = async () => {
    if (pendingFile && selectedSheets.length > 0) {
      const sheetsToImport = sheetOptions.filter((s) => selectedSheets.includes(s.title));
      const blobs = [];
      for (const sheet of sheetsToImport) {
        const blob = await fetchSheetData(pendingFile.file, sheet);
        if (blob) {
          blobs.push(blob);
        }
      }
      if (blobs.length > 0) {
        onCSVBlobsCreated(blobs);
      }
      onClose();
      setIsPickerOpen(false);
      setSheetOptions([]);
      setSelectedSheets([]);
      setPendingFile(null);
    }
  };

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getOAuthToken();
        setOauthToken(token);
      } catch (error) {
        console.error("Error acquiring OAuth token:", error);
      }
    };
    if (pickerScriptLoaded && isRequested && !oauthToken) fetchToken();
  }, [isRequested, oauthToken, pickerScriptLoaded, setOauthToken]);

  useEffect(() => {
    if (isRequested && !isPickerOpen && pickerScriptLoaded && window.google && oauthToken) {
      setIsPickerOpen(true);
      openPicker({
        clientId: GOOGLE_CLIENT_ID,
        developerKey: GOOGLE_API_KEY,
        viewId: "SPREADSHEETS",
        multiselect: false,
        showUploadView: true,
        showUploadFolders: true,
        supportDrives: true,
        token: oauthToken, // Pass the acquired OAuth token to Picker
        callbackFunction: (data) => {
          switch (data.action) {
            case "loaded":
              break;
            case "picked":
              if (data.docs && data.docs.length > 0) {
                // In the Picker callback, only call handleSelectSpreadsheetFromPicker if not waiting for sheet selection
                if (!pendingFile) {
                  handleSelectSpreadsheetFromPicker(data.docs[0]);
                }
              }
              break;
            case "cancel":
              onClose();
              setIsPickerOpen(false);
              break;
            default:
              console.log("Unknown picker action:", data.action);
              break;
          }
        },
      });
    }
  }, [isPickerOpen, openPicker, pickerScriptLoaded, handleSelectSpreadsheetFromPicker, oauthToken, onClose, pendingFile, isRequested]);

  return (
    <>
      {sheetOptions.length > 1 && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h1>Multiple Sheets Found</h1>
            <p>Select the sheets you would like to import</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, padding: 16 }}>
              {sheetOptions.map((properties) => (
                <button
                  key={properties.title}
                  style={{
                    padding: "12px 12px",
                    border: selectedSheets.includes(properties.title) ? "2px solid #var(--Blue3)" : "1px solid var(--Grey7)",
                    color: "var(--Grey7)",
                    fontSize: 14,
                    borderRadius: 4,
                    cursor: "pointer",
                    backgroundColor: selectedSheets.includes(properties.title) ? "rgba(25, 118, 210, 0.1)" : "transparent",
                  }}
                  onClick={() => handleSheetSelect(properties.title)}
                >
                  {properties.title}
                </button>
              ))}
            </div>
            <div className="modal-buttons">
              <button
                onClick={() => {
                  onClose();
                  setIsPickerOpen(false);
                  setSheetOptions([]);
                  setSelectedSheets([]);
                  setPendingFile(null);
                }}
                className="cancel-btn"
              >
                Cancel
              </button>

              <button
                onClick={handleImportSelected}
                className="confirm-btn"
                style={{ marginRight: "10px", padding: "8px 16px", cursor: "pointer" }}
                disabled={selectedSheets.length === 0}
              >
                Import Selected
              </button>

            </div>
          </div>
        </div>
      )}
    </>
  );
};

GooglePicker.propTypes = {
  isRequested: PropTypes.bool.isRequired,
  onCSVBlobsCreated: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  oauthToken: PropTypes.string,
  setOauthToken: PropTypes.func.isRequired,
};

export default GooglePicker;
